import type { TypeExposition } from '@artosera/shared';
import type { JSX } from 'react';
import styles from './Cimaise.module.css';

export interface CimaiseProps {
  /** Œuvres accrochées, dans l'ordre d'accrochage : une silhouette par œuvre. */
  oeuvres: readonly string[];
  type: TypeExposition;
  /** Numéro de stand d'une foire, porté sur le fronton. */
  stand?: string | null;
  taille?: 'carte' | 'couverture';
}

/** Au-delà, l'élévation devient illisible : le reste est résumé par « +n ». */
const MAXIMUM_VISIBLE = 6;

type Format = 'portrait' | 'paysage' | 'carre';
const FORMATS: readonly Format[] = ['portrait', 'paysage', 'portrait', 'carre', 'paysage'];

/** Format stable par œuvre : même identifiant, même silhouette d'un rendu à l'autre. */
function formatDe(id: string): Format {
  let somme = 0;
  for (const caractere of id) somme = (somme * 31 + caractere.charCodeAt(0)) % 9973;
  return FORMATS[somme % FORMATS.length] ?? 'portrait';
}

/**
 * Élévation schématique de l'accrochage : le mur (ou le stand d'une foire)
 * et une silhouette par œuvre accrochée, dans l'ordre. Purement visuelle
 * (`aria-hidden`) : le nombre d'œuvres est toujours écrit à côté.
 */
export function Cimaise({
  oeuvres,
  type,
  stand = null,
  taille = 'carte',
}: CimaiseProps): JSX.Element {
  const visibles = oeuvres.slice(0, MAXIMUM_VISIBLE);
  const reste = oeuvres.length - visibles.length;
  const estStand = type === 'foire';

  return (
    <div
      className={styles.cimaise}
      data-taille={taille}
      data-stand={estStand ? 'true' : 'false'}
      aria-hidden
    >
      {estStand ? <span className={styles.fronton}>{stand ?? ''}</span> : null}
      <div className={styles.mur}>
        <span className={styles.rail} />
        {visibles.length === 0 ? (
          <span className={styles.emplacement} />
        ) : (
          visibles.map((id, index) => (
            <span
              key={`${id}-${String(index)}`}
              className={styles.cadre}
              data-format={formatDe(id)}
            >
              <span className={styles.oeuvre} />
            </span>
          ))
        )}
        {reste > 0 ? <span className={styles.reste}>+{reste}</span> : null}
      </div>
      <span className={styles.sol} />
    </div>
  );
}
